import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../../lib/api';
import { disciplineLabel } from '../../../lib/disciplines';
import { Briefcase, Trash2, Printer, FileSignature, UserPlus, Banknote, FileText, Contact, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Modal, Row } from './ui';
import { TaxPanel } from './TaxPanel';
import { PayrollPanel, PayStubModal } from './payroll';
import { LetterModal } from './LetterModal';
import { CardModal } from './CardModal';
import { SignModal } from './SignModal';

/* ---------------- Detail drawer: contract, signing, print, payroll, letter, card ---------------- */

function HRDetail({ emp, onClose, onEdit, onDelete, onChanged }) {
  const nav = useNavigate();
  const [contract, setContract] = useState(null);
  const [signing, setSigning] = useState(false);
  const [showLetter, setShowLetter] = useState(false);
  const [showCard, setShowCard] = useState(false);
  const [stubRun, setStubRun] = useState(null);
  const [stubBusy, setStubBusy] = useState(false);
  const [tempPw, setTempPw] = useState(null);
  const [printSel, setPrintSel] = useState({ profile: true, pay: true, contract: true, signatures: true });

  useEffect(() => {
    api.get(`/hr/employees/${emp.employee_id}/contract`).then(r => setContract(r.data)).catch(() => {});
  }, [emp.employee_id]);

  const createAccount = async () => {
    try {
      const { data } = await api.post(`/hr/employees/${emp.employee_id}/create-account`);
      setTempPw(data.temporary_password);
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not create the account');
    }
  };

  // Iter 146 — one-click pay stub, auto-filled from the staff record.
  const createPayStub = async () => {
    setStubBusy(true);
    try {
      const period = new Date().toISOString().slice(0, 7);
      const { data } = await api.post(`/hr/employees/${emp.employee_id}/payroll-runs`, {
        period,
        vacation_start: emp.vacation_start || null, vacation_end: emp.vacation_end || null,
        extra_vacation_dates: emp.extra_vacation_dates || null,
        sick_dates: emp.sick_dates || null, bereavement_dates: emp.bereavement_dates || null,
        maternity_start: emp.maternity_start || null, maternity_end: emp.maternity_end || null,
        payment_reference: emp.payment_reference || `Salary ${period}`,
      });
      setStubRun(data);
      toast.success('Pay stub prepared from the staff record — review, print or process the payment.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not prepare the pay stub — set a salary on the record first.');
    } finally { setStubBusy(false); }
  };

  const doPrint = () => {
    document.body.classList.add('hr-printing');
    window.print();
    setTimeout(() => document.body.classList.remove('hr-printing'), 500);
  };

  return (
    <Modal onClose={onClose} wide>
      <div className="flex items-start justify-between gap-3 print:hidden">
        <div className="flex items-center gap-3">
          <div className="w-14 h-14 rounded-full bg-[#F2EBDB] overflow-hidden flex items-center justify-center">
            {emp.photo_url ? <img src={emp.photo_url} alt="" className="w-full h-full object-cover"/> : <Briefcase className="w-6 h-6 text-brand"/>}
          </div>
          <div>
            <h2 className="font-heading font-bold text-xl">{emp.first_name} {emp.last_name}</h2>
            <div className="text-xs text-fg-muted">{emp.professional_title || emp.staff_type} · {(emp.hire_type || '').replace(/_/g,' ')} {emp.discipline ? `· ${disciplineLabel(emp.discipline)}` : ''}</div>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap justify-end">
          <button className="btn-secondary text-xs" onClick={onEdit} data-testid="hr-edit-btn">Edit</button>
          <button className="btn-secondary text-xs inline-flex items-center gap-1" onClick={() => setShowLetter(true)} data-testid="hr-letter-btn">
            <FileText className="w-3.5 h-3.5"/> Employment letter
          </button>
          <button className="btn-secondary text-xs inline-flex items-center gap-1" onClick={() => setShowCard(true)} data-testid="hr-card-btn">
            <Contact className="w-3.5 h-3.5"/> Business card
          </button>
          <button className="btn-secondary text-xs inline-flex items-center gap-1" onClick={createPayStub} disabled={stubBusy} data-testid="hr-paystub-btn">
            {stubBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <Banknote className="w-3.5 h-3.5"/>} Create pay stub
          </button>
          {!emp.linked_user_id && (emp.staff_type === 'clinician' || emp.staff_type === 'reception') && (
            <button className="btn-secondary text-xs inline-flex items-center gap-1" onClick={createAccount} data-testid="hr-create-account-btn">
              <UserPlus className="w-3.5 h-3.5"/> Create {emp.staff_type} account
            </button>
          )}
          <button className="btn-secondary text-xs text-[#B83A3A]" onClick={onDelete} data-testid="hr-delete-btn"><Trash2 className="w-3.5 h-3.5"/></button>
        </div>
      </div>

      {/* Print selection */}
      <div className="mt-3 flex flex-wrap items-center gap-3 text-xs print:hidden surface border border-soft rounded-lg px-3 py-2">
        <Printer className="w-4 h-4 text-brand"/>
        <span className="font-medium">Print:</span>
        {Object.entries({ profile: 'Profile', pay: 'Pay & tax', contract: 'Contract', signatures: 'Signatures' }).map(([k, lbl]) => (
          <label key={k} className="inline-flex items-center gap-1 cursor-pointer">
            <input type="checkbox" checked={printSel[k]} onChange={e => setPrintSel(s => ({ ...s, [k]: e.target.checked }))} data-testid={`hr-print-${k}`}/>
            {lbl}
          </label>
        ))}
        <button className="btn-primary !py-1 text-xs ml-auto" onClick={doPrint} data-testid="hr-print-btn">Print selected</button>
      </div>

      <div id="hr-print-area" className="mt-4 space-y-4">
        {printSel.profile && (
          <div className="surface border border-soft rounded-lg p-4">
            <h3 className="font-heading font-semibold mb-2">Profile</h3>
            <dl className="grid md:grid-cols-2 gap-x-6 gap-y-1 text-sm">
              <Row k="Email" v={emp.email}/><Row k="Phone" v={emp.phone}/>
              <Row k="Staff type" v={emp.staff_type}/><Row k="Outsourced company" v={emp.outsourced_company}/>
              <Row k="License #" v={emp.professional_license_no}/>
              <Row k="Malpractice" v={emp.malpractice_insurer ? `${emp.malpractice_insurer} · ${emp.malpractice_policy_no || ''} · until ${emp.malpractice_expiry || '—'}` : null}/>
              <Row k="Hired" v={emp.date_hired}/><Row k="Separated" v={emp.date_separated ? `${emp.date_separated} (${emp.separation_type || ''})` : null}/>
              <Row k="Hiring manager" v={emp.hiring_manager_name}/>
              <Row k="CV" v={emp.cv_link}/>
            </dl>
            {emp.bio_cv && <p className="text-xs text-fg-muted mt-2 whitespace-pre-wrap">{emp.bio_cv}</p>}
          </div>
        )}
        {printSel.pay && (
          <div className="surface border border-soft rounded-lg p-4">
            <h3 className="font-heading font-semibold mb-2">Pay, schedule & perks</h3>
            <dl className="grid md:grid-cols-2 gap-x-6 gap-y-1 text-sm">
              <Row k="Schedule" v={`${(emp.schedule_type || '').replace('_','-')} · ${emp.weekly_hours || '—'} h/week`}/>
              <Row k="Hourly wage" v={emp.hourly_wage ? `${emp.hourly_wage} ${emp.currency}` : null}/>
              <Row k="Monthly salary" v={emp.monthly_salary ? `${emp.monthly_salary} ${emp.currency}` : null}/>
              <Row k="Payment" v={(emp.payment_form || '').replace(/_/g,' ')}/>
              <Row k="Bank" v={emp.bank_name_branch ? `${emp.bank_name_branch} · ${emp.bank_full_legal_name || ''}` : null}/>
              <Row k="Account" v={emp.bank_iban || emp.bank_account_number || null}/>
              <Row k="Net salary" v={emp.salary_net_amount ? `${emp.salary_net_amount} ${emp.currency}` : null}/>
              <Row k="Pay reference" v={emp.payment_reference}/>
              <Row k="Vacation" v={emp.vacation_days != null ? `${emp.vacation_days} days/yr` : null}/>
              <Row k="Holiday extra" v={emp.holiday_pay_extra_pct != null ? `+${emp.holiday_pay_extra_pct}%` : null}/>
              <Row k="Uniform" v={`first free${emp.additional_uniform_fee ? ` · additional ${emp.additional_uniform_fee} ${emp.currency}` : ''}`}/>
              <Row k="Perks" v={(emp.perks || []).map(p => p.replace(/_/g,' ')).join(', ') || emp.custom_perks}/>
            </dl>
            <TaxPanel f={emp}/>
          </div>
        )}

        <PayrollPanel emp={emp}/>

        {printSel.contract && contract && (
          <div className="surface border border-soft rounded-lg p-4" data-testid="hr-contract">
            <h3 className="font-heading font-semibold mb-1">Contract — {contract.clinic_name} ⇄ {contract.employee_name}</h3>
            <p className="text-[10px] text-fg-muted mb-3">{contract.disclaimer}</p>
            {contract.sections.map((s, i) => (
              <div key={i} className="mb-2.5">
                <div className="text-xs font-bold uppercase tracking-wide">{s.title}</div>
                <p className="text-xs text-fg-secondary whitespace-pre-wrap">{s.body}</p>
              </div>
            ))}
          </div>
        )}
        {printSel.signatures && (
          <div className="surface border border-soft rounded-lg p-4">
            <div className="flex items-center justify-between mb-2 print:hidden">
              <h3 className="font-heading font-semibold">Signatures</h3>
              <button className="btn-secondary text-xs inline-flex items-center gap-1" onClick={() => setSigning(true)} data-testid="hr-sign-btn">
                <FileSignature className="w-3.5 h-3.5"/> Sign digitally
              </button>
            </div>
            {(emp.signatures || []).length === 0 && <p className="text-xs text-fg-muted">Not signed yet. Use "Sign digitally" or print the contract for wet-ink signatures.</p>}
            <div className="grid md:grid-cols-2 gap-3">
              {(emp.signatures || []).map(s => (
                <div key={s.sig_id} className="border border-soft rounded-md p-2 text-xs">
                  <div className="font-semibold">{s.signer_name} <span className="text-fg-muted">({s.signer_role})</span></div>
                  {s.signature_data_url
                    ? <img src={s.signature_data_url} alt="signature" className="h-12 mt-1"/>
                    : <div className="font-serif italic text-lg mt-1">{s.typed_signature}</div>}
                  <div className="text-fg-muted mt-1">{new Date(s.signed_at).toLocaleString()}</div>
                </div>
              ))}
            </div>
            <div className="hidden print:grid grid-cols-2 gap-8 mt-8 text-xs">
              <div><div className="border-t border-fg pt-1">Clinic representative — date & signature</div></div>
              <div><div className="border-t border-fg pt-1">Professional — date & signature</div></div>
            </div>
          </div>
        )}
      </div>

      {signing && <SignModal emp={emp} onClose={() => setSigning(false)} onSigned={() => { setSigning(false); onChanged(); onClose(); }}/>}
      {showLetter && <LetterModal emp={emp} onClose={() => setShowLetter(false)}/>}
      {showCard && <CardModal emp={emp} onClose={() => setShowCard(false)}/>}
      {stubRun && <PayStubModal run={stubRun} emp={emp} onClose={() => setStubRun(null)}/>}
      {tempPw && (
        <Modal onClose={() => { setTempPw(null); if (emp.staff_type === 'clinician') nav('/admin/clinicians'); }}>
          <h3 className="font-heading font-bold text-lg mb-2">Account created</h3>
          <p className="text-sm text-fg-muted mb-3">Share this temporary password securely — it is shown only once:</p>
          <div className="font-mono text-lg font-bold border border-soft rounded-md px-4 py-3 bg-[#F8F5EC] select-all" data-testid="hr-temp-password">{tempPw}</div>
          <div className="flex justify-end mt-4">
            <button className="btn-primary text-sm" onClick={() => { setTempPw(null); if (emp.staff_type === 'clinician') nav('/admin/clinicians'); }}>Done</button>
          </div>
        </Modal>
      )}
    </Modal>
  );
}

export { HRDetail };
